import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { yupResolver } from '@hookform/resolvers/yup';

import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import Loader from '../../../components/Loader';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormTextField from '../../../components/formFields/FormTextField';
import { DEDUCTION_TYPES, PAYMENT_METHOD_OPTIONS } from '../../../constants/constants';
import { deductionDraftEditFormSchema } from '../../../helpers/formSchemas/formSchemas';
import { DEDUCTION_EDIT_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import { useEffect, useRef, useState } from 'react';
import FormAutocompleteFieldServerSearch from '../../../components/formFields/FormAutocompleteFieldServerSearch';
import { PaginationQueryParams, SelectOption } from '../../../types';
import {
  ActiveVendorAccountOption,
  getActiveVendorAccounts,
  getVendorPaymentMethodDetails,
} from '../../../api/vendorApi/vendorAccountsApi';
import {
  getVendorDefaultPaymentAccountId,
  getVendorPaymentAccountOptions,
} from '../../../helpers/vendorPaymentAccounts';
import { mapVendorsQueryResults } from '../../CasesView/helpers';
import AddButton from '../../../components/buttons/AddButton';
import FormLinkButton from '../../../components/buttons/FormLinkButton';
import FindNetSuiteVendorPopup from '../../../components/FindNetSuiteVendorPopup';
import CommonButton from '../../../components/buttons/CommonButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import Popup from '../../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../../constants/popupDescriptions';
import { renderPaymentAccountInfo, renderPaymentAccountOption, renderPaymentAccountSelectedValue } from '../paymentAccountDisplayHelpers';

const DEDUCTION_PAYMENT_METHOD_OPTIONS = PAYMENT_METHOD_OPTIONS.filter(
  (option) => String(option.id).toUpperCase() !== 'V',
);
import { accountingVendorType } from '../../VendorsView/helpers';

const useStyles = makeStyles()((theme: Theme) => ({
  vendorSuggestionsBox: {
    width: '100%',
  },
  vendorSuggestionsTitle: {
    fontWeight: 600,
    color: theme.palette.common.white,
    marginBottom: 8,
    fontSize: 14,
    fontFamily: 'Inter',
  },
  vendorSuggestionItem: {
    cursor: 'pointer',
    background: theme.palette.secondary.dark,
    color: theme.palette.common.white,
    padding: '10px 16px',
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: 'Inter',
    fontSize: 15,
    transition: 'background 0.2s, color 0.2s',
    '&:hover': {
      borderColor: theme.palette.info.main,
      background: theme.palette.secondary.light,
    },
  },
  selectedVendorSuggestion: {
    borderColor: theme.palette.info.main,
    background: theme.palette.secondary.light,
  },
  popupContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.light,
      maxWidth: '550px',
      width: '100%',
      padding: '32px',
      boxSizing: 'border-box',
      borderRadius: '16px',
      maxHeight: '95%',
    },
    '& .MuiDialogTitle-root': {
      padding: 0,
      fontSize: '34px',
      fontWeight: 600,
      lineHeight: '120%',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
    },

    '& .MuiDialogContent-root': {
      padding: 0,
      display: 'flex',
      justifyContent: 'center',
      overflowY: 'auto', // ✅ add this
      overflowX: 'hidden', // ✅ prevent horizontal scroll

      '&::-webkit-scrollbar': {
        width: '5px',
        borderRadius: '20px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 20,
        backgroundColor: theme.palette.secondary.dark,
      },
    },

    '& .MuiDialogContentText-root': {
      color: theme.palette.secondary.main,
    },
  },
  popupActions: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '416px',
    width: '100%',
    margin: '0 auto',
    gap: '16px',
    padding: 0,
  },
  popupButton: {
    color: theme.palette.info.main,

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.3)',
    },

    '&:hover': {
      background: 'rgba(144, 202, 249, 0.08)',
    },
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '416px',
    width: '100%',
  },
  formFieldsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    alignItems: 'center',
    padding: '32px 0 32px 0',
  },
  checkboxWrapper: {
    width: '100%',

    '& label': {
      marginRight: 0,
    },
  },
}));

export type DeductionPopupData = {
  vendorName: {
    id: string;
    label: string;
    defaultPaymentMethod?: string;
    accounting?: { isSynced: boolean; externalVendorId: string };
  } | null;
  id: number;
  type: string;
  amount: number;
  vendorParticipationId: string | null;
  vendorPaymentAccountId?: string | number | null;
  vendorPaymentAccountName?: string | null;
  paymentAccountId?: string | number | null;
  paymentAccountName?: string | null;
  // vendorName: string | null;
  vendorSuggestions: Array<{
    id: number;
    vendorName: string;
    similarity: number;
    paymentMethod: string;
    provisioning: {
      ach: { isProvisioned: boolean };
      check: { isProvisioned: boolean };
    };
    accounting?: {
      isSynced: boolean;
      externalVendorId: string;
    };
  }>;
  paymentMethod: string;
  disposition: string;
  memo: string;
  matchingMode: string | null;
  originalText: string;
  description?: string;
  provisioning: {
    ach: { isProvisioned: boolean };
    check: { isProvisioned: boolean };
  };
};

interface EditDraftDeductionPopupProps {
  isOpen: boolean;
  loading?: boolean;
  canViewVendors?: boolean;
  canCreateVendors?: boolean;
  onSuccess?: () => void;
  onFailed?: () => void;
  onCancel: () => void;
  initialData?: DeductionPopupData;
  settlementId: number;
  updatedData?: (data: unknown) => void;
  isAccountingIntegrated?: boolean;
}

type DraftDeductionFormValues = {
  id?: number | string;
  type: string;
  vendorName?: {
    label?: string;
    id?: string;
    defaultPaymentMethod?: string;
    accounting?: { isSynced?: boolean; externalVendorId?: string | null } | null;
  } | null;
  amount: number;
  vendorParticipationId?: string | null;
  description?: string;
  paymentMethod?: string | null;
  paymentAccount?: { id?: string; label?: string } | null;
  matchingMode?: string | null;
  originalText?: string;
};

const EditDraftDeductionPopup = ({
  isOpen,
  loading,
  canViewVendors,
  canCreateVendors,
  onCancel,
  initialData,
  onSuccess,
  updatedData,
  isAccountingIntegrated,
}: EditDraftDeductionPopupProps) => {
  const { classes } = useStyles();
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isNetSuitePopupOpen, setIsNetSuitePopupOpen] = useState(false);


  const vendorType = accountingVendorType;

  const formMethods = useForm<DraftDeductionFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(deductionDraftEditFormSchema) as never,
    defaultValues: DEDUCTION_EDIT_FORM_DEFAULT_VALUES as DraftDeductionFormValues,
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { isValid, isDirty },
  } = formMethods;

  const type = watch('type');
  const [matchingMode, setMatchingMode] = useState<string | null>(null);

  const vendorAccountIdValue = watch('vendorParticipationId');
  const vendorAccountValue = watch('vendorName') as {
    id: string;
    label: string;
    defaultPaymentMethod?: string;
    accounting?: { isSynced?: boolean; externalVendorId?: string | null };
  } | null;
  const paymentMethodValue = watch('paymentMethod');
  const paymentAccountValue = watch('paymentAccount');
  const isPayDisposition = ['PAY', 'Pay'].includes(initialData?.disposition || '');
  const requiresPaymentAccount =
    isPayDisposition && ['ACH', 'CHECK'].includes((paymentMethodValue || '').toUpperCase());

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [vendorSuggestions, setVendorSuggestions] = useState<any[]>([]);
  const [activeVendors, setActiveVendors] = useState<ActiveVendorAccountOption[]>([]);
  const [vendorPaymentAccountsById, setVendorPaymentAccountsById] = useState<
    Record<
      string,
      {
        achAccounts?: ActiveVendorAccountOption['achAccounts'];
        checkAccounts?: ActiveVendorAccountOption['checkAccounts'];
      }
    >
  >({});
  const [paymentAccountOptions, setPaymentAccountOptions] = useState<
    Array<{
      id: string;
      label: string;
      subtitle?: string;
      isLastUsedAccount?: boolean;
      isUsedAccount?: boolean;
      isNotUsedAccount?: boolean;
      isFailedAccount?: boolean;
    }>
  >([]);


  const [isPaymentMethodMissing, setIsPaymentMethodMissing] = useState(false);
  const [isPaymentProvisioningIssue, setIsPaymentProvisioningIssue] = useState(false);
  const [isRefreshingVendors, setIsRefreshingVendors] = useState(false);
  const [isSelectingNetSuiteVendor, setIsSelectingNetSuiteVendor] = useState(false);
  const [vendorOptionsVersion, setVendorOptionsVersion] = useState(0);

  const getResetFormValues = (): DraftDeductionFormValues => ({
    id: initialData?.id,
    type: initialData?.type || '',
    amount: initialData?.amount ?? 0,
    vendorParticipationId: initialData?.vendorParticipationId ?? null,
    vendorName: initialData?.vendorName ?? undefined,
    description: initialData?.description ?? '',
    paymentMethod: initialData?.paymentMethod ?? '',
    paymentAccount:
      (initialData?.vendorPaymentAccountId ?? initialData?.paymentAccountId)
        ? {
          id: String(initialData?.vendorPaymentAccountId ?? initialData?.paymentAccountId),
          label: String(initialData?.vendorPaymentAccountName ?? initialData?.paymentAccountName ?? ''),
        }
        : null,
    matchingMode: initialData?.matchingMode ?? null,
  });


  // Check if any vendors have accounting fields
  // const hasVendorsWithAccounting = () => {
  //   // Check in vendor suggestions
  //   const hasSuggestionsWithAccounting = vendorSuggestions.some((v) => v.accounting);
  //   // Check in active vendors
  //   const hasActiveVendorsWithAccounting = activeVendors.some((v) => v.accounting);
  //   return hasSuggestionsWithAccounting || hasActiveVendorsWithAccounting;
  // };

  const selectedPaymentAccount = paymentAccountOptions.find(
    (item) => item.id === String(paymentAccountValue?.id),
  );

  const [isFailedAccountConfirmOpen, setIsFailedAccountConfirmOpen] = useState<boolean>(false);
  const pendingSubmitValuesRef = useRef<DraftDeductionFormValues | null>(null);

  useEffect(() => {
    const selectedVendor = activeVendors.find(
      (vendor) => String(vendor.id) === String(vendorAccountValue?.id),
    );

    if ((paymentMethodValue || '').toUpperCase() === 'V') {
      setIsPaymentMethodMissing(false);
      return;
    }

    const selectedActivePaymentMethod = selectedVendor?.activePaymentMethod || [];
    setIsPaymentMethodMissing(
      !!paymentMethodValue && !selectedActivePaymentMethod.includes(paymentMethodValue),
    );
  }, [paymentMethodValue, vendorAccountIdValue, activeVendors]);

  useEffect(() => {
    const selectedVendor = activeVendors.find(
      (vendor) => String(vendor.id) === String(vendorAccountValue?.id),
    );
    const normalizedPaymentMethod = (paymentMethodValue || '').trim().toUpperCase();
    const selectedPaymentAccountId = paymentAccountValue?.id
      ? String(paymentAccountValue.id)
      : '';

    if (!selectedVendor || !selectedPaymentAccountId || !requiresPaymentAccount) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const provisioningList =
      normalizedPaymentMethod === 'ACH'
        ? selectedVendor.activeBankAccounts || []
        : normalizedPaymentMethod === 'CHECK'
          ? selectedVendor.activeMailingAddresses || []
          : [];

    if (!provisioningList.length) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const selectedProvisioningAccount = provisioningList.find(
      (account) => String(account.id) === selectedPaymentAccountId,
    );

    setIsPaymentProvisioningIssue(
      !!selectedProvisioningAccount && selectedProvisioningAccount.provisionStatus !== 'Completed',
    );
  }, [
    paymentMethodValue,
    paymentAccountValue?.id,
    vendorAccountValue?.id,
    activeVendors,
    requiresPaymentAccount,
  ]);

  useEffect(() => {
    const currentVendorId = vendorAccountValue?.id ? String(vendorAccountValue.id) : null;
    const currentVendorParticipationId = vendorAccountIdValue
      ? String(vendorAccountIdValue)
      : null;

    if (currentVendorId !== currentVendorParticipationId) {
      setValue('vendorParticipationId', currentVendorId, { shouldDirty: false });
    }

    if (vendorAccountValue?.defaultPaymentMethod && !paymentMethodValue) {
      setValue('paymentMethod', vendorAccountValue.defaultPaymentMethod, { shouldDirty: false });
    }
  }, [vendorAccountValue?.id, vendorAccountValue?.defaultPaymentMethod, paymentMethodValue, setValue, vendorAccountIdValue]);

  const applyVendorPaymentDefaults = async (
    selectedVendor: SelectOption | null,
    shouldDirty = true,
  ) => {
    const vendorId = selectedVendor?.id ? String(selectedVendor.id) : '';

    setValue('vendorParticipationId', vendorId || null, {
      shouldDirty,
      shouldValidate: true,
    });
    setValue('paymentAccount', null, { shouldDirty, shouldValidate: true });

    if (!vendorId) {
      setValue('paymentMethod', '', {
        shouldDirty,
        shouldValidate: true,
      });
      return;
    }

    const selectedActiveVendor = activeVendors.find(
      (vendor) => String(vendor.id) === vendorId,
    );

    const fallbackDefaultPaymentMethod =
      selectedVendor?.defaultPaymentMethod ||
      selectedActiveVendor?.defaultVendorPaymentMethod ||
      '';

    setValue('paymentMethod', fallbackDefaultPaymentMethod, {
      shouldDirty,
      shouldValidate: true,
    });

    const parsedVendorId = Number(vendorId);

    if (!Number.isFinite(parsedVendorId)) {
      return;
    }

    try {
      const response = await getVendorPaymentMethodDetails(parsedVendorId);

      const latestSelectedVendorId = getValues('vendorParticipationId');
      if (String(latestSelectedVendorId || '') !== vendorId) {
        return;
      }

      setVendorPaymentAccountsById((prev) => ({
        ...prev,
        [vendorId]: {
          achAccounts: response.data.achAccounts,
          checkAccounts: response.data.checkAccounts,
        },
      }));

      const apiDefaultPaymentMethod =
        response.data.defaultVendorPaymentMethod || fallbackDefaultPaymentMethod;

      setValue('paymentMethod', apiDefaultPaymentMethod, {
        shouldDirty,
        shouldValidate: true,
      });

      const latestVendorValue = getValues('vendorName');
      if (latestVendorValue && String(latestVendorValue.id || '') === vendorId) {
        setValue(
          'vendorName',
          {
            ...latestVendorValue,
            defaultPaymentMethod: apiDefaultPaymentMethod,
          },
          { shouldDirty: false, shouldValidate: false },
        );
      }
    } catch {
      const latestSelectedVendorId = getValues('vendorParticipationId');
      if (String(latestSelectedVendorId || '') !== vendorId) {
        return;
      }

      setValue('paymentMethod', fallbackDefaultPaymentMethod, {
        shouldDirty,
        shouldValidate: true,
      });
    }
  };

  useEffect(() => {
    if (!vendorAccountIdValue || !paymentMethodValue || !requiresPaymentAccount) {
      setPaymentAccountOptions([]);
      setValue('paymentAccount', null, { shouldDirty: false, shouldValidate: true });
      return;
    }

    const vendorIdKey = String(vendorAccountIdValue);
    const vendorId = Number(vendorAccountIdValue);

    if (!vendorPaymentAccountsById[vendorIdKey] && Number.isFinite(vendorId)) {
      getVendorPaymentMethodDetails(vendorId)
        .then((response) => {
          setVendorPaymentAccountsById((prev) => ({
            ...prev,
            [vendorIdKey]: {
              achAccounts: response.data.achAccounts,
              checkAccounts: response.data.checkAccounts,
            },
          }));
        })
        .catch(() => {
          setVendorPaymentAccountsById((prev) => ({
            ...prev,
            [vendorIdKey]: {
              achAccounts: [],
              checkAccounts: [],
            },
          }));
        });
    }

    const selectedVendor = activeVendors.find(
      (vendor) => String(vendor.id) === String(vendorAccountIdValue),
    );
    const selectedVendorAccounts = vendorPaymentAccountsById[vendorIdKey];

    const vendorForPaymentAccounts = selectedVendor
      ? {
        ...selectedVendor,
        achAccounts: selectedVendorAccounts?.achAccounts,
        checkAccounts: selectedVendorAccounts?.checkAccounts,
      }
      : selectedVendorAccounts;

    const normalizedPaymentMethod = (paymentMethodValue || '').trim().toUpperCase();

    const accountDetails: Array<{
      id: number | string;
      isLastUsedAccount?: boolean;
      isUsedAccount?: boolean;
      isNotUsedAccount?: boolean;
      isFailedAccount?: boolean;
    }> =
      normalizedPaymentMethod === 'ACH'
        ? selectedVendorAccounts?.achAccounts || []
        : normalizedPaymentMethod === 'CHECK'
          ? selectedVendorAccounts?.checkAccounts || []
          : [];

    const options = getVendorPaymentAccountOptions(
      vendorForPaymentAccounts,
      paymentMethodValue,
    ).map((item) => {
      const account = accountDetails.find(
        (account) => String(account.id) === String(item.id),
      );

      const subtitleWithoutAccountType =
        item.paymentMethod === 'ACH'
          ? item.subtitle.split(' ').slice(-1).join(' ')
          : item.subtitle;

      return {
        id: String(item.id),
        label: subtitleWithoutAccountType
          ? `${item.label} - ${subtitleWithoutAccountType}`
          : item.label,
        subtitle: item.subtitle,
        isLastUsedAccount: account?.isLastUsedAccount,
        isUsedAccount: account?.isUsedAccount,
        isNotUsedAccount: account?.isNotUsedAccount,
        isFailedAccount: account?.isFailedAccount,
      };
    });

    setPaymentAccountOptions(options);

    const hasCurrentSelection =
      !!paymentAccountValue?.id && options.some((item) => item.id === String(paymentAccountValue.id));
    if (hasCurrentSelection) return;

    const initialPaymentAccountId = initialData?.vendorPaymentAccountId
      ? String(initialData.vendorPaymentAccountId)
      : initialData?.paymentAccountId
        ? String(initialData.paymentAccountId)
        : '';
    const initialOption = options.find((item) => item.id === initialPaymentAccountId);
    if (initialOption) {
      setValue(
        'paymentAccount',
        { id: initialOption.id, label: initialOption.label },
        { shouldDirty: false, shouldValidate: true },
      );
      return;
    }

    const defaultAccountId = getVendorDefaultPaymentAccountId(
      vendorForPaymentAccounts,
      paymentMethodValue,
    );
    if (defaultAccountId) {
      const defaultOption = options.find((item) => item.id === String(defaultAccountId));
      if (defaultOption) {
        setValue(
          'paymentAccount',
          { id: defaultOption.id, label: defaultOption.label },
          { shouldDirty: false, shouldValidate: true },
        );
        return;
      }
    }

    setValue('paymentAccount', null, { shouldDirty: false, shouldValidate: true });
  }, [
    vendorAccountIdValue,
    paymentMethodValue,
    activeVendors,
    vendorPaymentAccountsById,
    paymentAccountValue?.id,
    setValue,
    requiresPaymentAccount,
    initialData?.vendorPaymentAccountId,
    initialData?.paymentAccountId,
  ]);

  // Fetch top 3 vendors when type is 'Lien' and popup is open
  useEffect(() => {
    if (initialData && initialData.vendorSuggestions) {
      setVendorSuggestions(
        initialData.vendorSuggestions.map((v) => ({
          id: String(v.id),
          label: v.vendorName,
          accounting: v.accounting,
        })),
      );
    } else {
      setVendorSuggestions([]);
    }
  }, [initialData]);

  useEffect(() => {
    if (initialData) {
      reset(getResetFormValues());
      setMatchingMode(initialData.matchingMode || null);
    }

    // handleGetActiveVendors({ page: 1, size: 1000, match: '' }, type);
  }, [initialData, reset]);

  const submitDeductionForm = async (values: DraftDeductionFormValues) => {
    setIsFormSubmitting(true);
    try {
      const selectedVendor = activeVendors.find(
        (vendor) => String(vendor.id) === String(values.vendorName?.id || ''),
      );
      const normalizedPaymentMethod = (values.paymentMethod || '').trim().toUpperCase();
      const selectedPaymentAccountId = values.paymentAccount?.id
        ? String(values.paymentAccount.id)
        : '';

      const achProvisioning =
        selectedVendor?.activeBankAccounts?.find(
          (account) => String(account.id) === selectedPaymentAccountId,
        )?.provisionStatus === 'Completed';

      const checkProvisioning =
        selectedVendor?.activeMailingAddresses?.find(
          (account) => String(account.id) === selectedPaymentAccountId,
        )?.provisionStatus === 'Completed';

      const provisioning = {
        ach: {
          isProvisioned:
            normalizedPaymentMethod === 'ACH'
              ? achProvisioning
              : initialData?.provisioning?.ach?.isProvisioned ?? false,
        },
        check: {
          isProvisioned:
            normalizedPaymentMethod === 'CHECK'
              ? checkProvisioning
              : initialData?.provisioning?.check?.isProvisioned ?? false,
        },
      };

      let formattedData;
      if (type === 'Lien' || type === 'SERVICE_PROVIDER') {
        formattedData = {
          ...values,
          amount: values.amount,
          vendorName: values.vendorName?.label,
          vendorParticipationId: Number(values.vendorName?.id) || null,
          vendorPaymentAccountId: values.paymentAccount?.id ? Number(values.paymentAccount.id) : null,
          vendorPaymentAccountName: values.paymentAccount?.label || null,
          matchingMode: values.vendorName?.label ? 'exact' : null,
          type: values.type,
          accounting: values.vendorName?.accounting,
          provisioning,
        };
      } else {
        formattedData = {
          ...values,
          amount: values.amount,
          vendorName: null,
          vendorParticipationId: null,
          vendorPaymentAccountId: null,
          vendorPaymentAccountName: null,
          matchingMode: null,
          type: 'Advance',
          description: values.description,
          provisioning: initialData?.provisioning,
        };
      }
      onSuccess && onSuccess();
      updatedData && updatedData(formattedData);
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const onSubmitForm = async (values: DraftDeductionFormValues) => {
    if (selectedPaymentAccount?.isFailedAccount) {
      pendingSubmitValuesRef.current = values;
      setIsFailedAccountConfirmOpen(true);
      return;
    }

    await submitDeductionForm(values);
  };

  const handleConfirmFailedAccountSubmit = async () => {
    setIsFailedAccountConfirmOpen(false);

    const values = pendingSubmitValuesRef.current;
    pendingSubmitValuesRef.current = null;

    if (values) {
      await submitDeductionForm(values);
    }
  };

  const handleCancelFailedAccountSubmit = () => {
    setIsFailedAccountConfirmOpen(false);
    pendingSubmitValuesRef.current = null;
  };

  const handleGetActiveVendors = async (params: PaginationQueryParams, type: string) => {
    let vendorTypeFilter: string | undefined;

    if (type === 'Lien') {
      vendorTypeFilter = 'LIEN_HOLDER';
    } else if (type === 'SERVICE_PROVIDER') {
      vendorTypeFilter = 'SERVICE_PROVIDER';
    }

    const filters = vendorTypeFilter ? { vendorType: vendorTypeFilter } : undefined;

    const response = await getActiveVendorAccounts(params.match, filters);

    setActiveVendors(response?.data);

    return {
      data: {
        resultSet: response.data as typeof activeVendors,
        totalCount: response.data.length,
      },
    };
  };

  const handleClose = () => {
    reset(getResetFormValues());
    onCancel();
  };

  // Helper to set vendorName field when suggestion is clicked
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setVendorName = (vendor: any) => {
    const selectedVendor = activeVendors.find((v) => String(v.id) === String(vendor.id));
    const defaultPaymentMethod = selectedVendor?.defaultVendorPaymentMethod || '';

    formMethods.setValue(
      'vendorName',
      {
        id: vendor.id,
        label: vendor.label,
        defaultPaymentMethod,
        accounting: vendor.accounting,
      },
      { shouldDirty: true },
    );

    void applyVendorPaymentDefaults(
      {
        id: String(vendor.id),
        label: vendor.label,
        defaultPaymentMethod,
        accounting: vendor.accounting,
      },
      true,
    );
  };

  const handleTypeChange = (value: string) => {
    setValue('type', value, { shouldDirty: true, shouldValidate: true });
    setVendorOptionsVersion((prev) => prev + 1);
    setValue(
      'vendorName',
      { id: '', label: '', defaultPaymentMethod: '' },
      { shouldDirty: true, shouldValidate: true },
    );
    setValue('paymentAccount', null, { shouldDirty: true, shouldValidate: true });
    setVendorSuggestions([]);
    // setValue('paymentMethod', '', { shouldDirty: true, shouldValidate: true });
  };

  const handleVendorSelectFromNetSuite = async (_vendorName: string, _vendorId: number, vendorInternalId?: string) => {
    setIsSelectingNetSuiteVendor(true);
    try {
      let vendorTypeFilter: string | undefined;
      if (type === 'Lien') vendorTypeFilter = 'LIEN_HOLDER';
      else if (type === 'SERVICE_PROVIDER') vendorTypeFilter = 'SERVICE_PROVIDER';
      const filters = vendorTypeFilter ? { vendorType: vendorTypeFilter } : undefined;

      const response = await getActiveVendorAccounts('', filters);
      const freshVendors = response.data as typeof activeVendors;

      const matchedVendor = freshVendors.find(
        (v) => v.accounting?.externalVendorId === vendorInternalId,
      );

      if (matchedVendor) {
        setActiveVendors(freshVendors);
        formMethods.setValue(
          'vendorName',
          {
            id: String(matchedVendor.id),
            label: matchedVendor.name,
            defaultPaymentMethod: matchedVendor.defaultVendorPaymentMethod,
            accounting: matchedVendor.accounting,
          },
          { shouldDirty: true },
        );

        await applyVendorPaymentDefaults(
          {
            id: String(matchedVendor.id),
            label: matchedVendor.name,
            defaultPaymentMethod: matchedVendor.defaultVendorPaymentMethod,
            accounting: matchedVendor.accounting,
          },
          true,
        );
      }
    } finally {
      setIsSelectingNetSuiteVendor(false);
    }
  };

  const handleRefreshVendors = () => {
    setIsRefreshingVendors(true);
    try {
      setVendorOptionsVersion((prev) => prev + 1);
    } finally {
      setIsRefreshingVendors(false);
    }
  };

  return (
    <Dialog className={classes.popupContainer} open={isOpen} onClose={handleClose} scroll="paper">
      <Box display="flex" justifyContent="flex-end">
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disableRipple
          sx={(theme) => ({
            color: theme.palette.common.white,
          })}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogTitle>{'Edit Deduction'}</DialogTitle>
      {loading || isSelectingNetSuiteVendor ? (
        <Box sx={{ margin: '30px' }}>
          <Loader colorType={'warning'} />
        </Box>
      ) : (
        <>
          <DialogContent>
            <FormProvider {...formMethods}>
              <Box className={classes.formContainer}>
                <Box className={classes.formFieldsWrapper}>
                  <FormSelectField
                    name="type"
                    label="Type *"
                    options={DEDUCTION_TYPES}
                    width="100%"
                    customOnChange={(value) => handleTypeChange(value)}
                  />
                  {(type === 'Lien' || type === 'SERVICE_PROVIDER') && matchingMode !== 'exact' && (
                    <FormTextField name="originalText" label="From Memo" disabled />
                  )}

                  {(type === 'Lien' || type === 'SERVICE_PROVIDER') && (
                    <>
                      <Box
                        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '5px' }}
                      >
                        <FormAutocompleteFieldServerSearch
                          name="vendorName"
                          label="Vendor Name *"
                          width="417px"
                          optionsQuery={(params) => handleGetActiveVendors(params, type)}
                          mapQueryResults={mapVendorsQueryResults}
                          isOptionEqualToValue={(option, value) =>
                            String(option.id) === String(value.id)
                          }
                          onValueChange={(option) => {
                            const selectedVendorId = String(option?.id || '');
                            const currentVendorId = String(getValues('vendorParticipationId') || '');
                            const currentPaymentMethod = getValues('paymentMethod');
                            const currentPaymentAccount = getValues('paymentAccount');
                            const shouldPreserveSavedPaymentDetails =
                              !isDirty &&
                              !!selectedVendorId &&
                              selectedVendorId === currentVendorId &&
                              !!currentPaymentMethod &&
                              !!currentPaymentAccount?.id;

                            if (shouldPreserveSavedPaymentDetails) {
                              return;
                            }

                            void applyVendorPaymentDefaults(option, true);
                          }}
                          fetchSize={100}
                          minSearchChars={3}
                          reloadKey={`${type}-${vendorOptionsVersion}`}
                        />
                        <Box>
                          <RefreshIcon
                            onClick={!isRefreshingVendors ? handleRefreshVendors : undefined}
                            sx={{
                              cursor: isRefreshingVendors ? 'default' : 'pointer',
                              color: (theme) => theme.palette.secondary.main,
                              animation: isRefreshingVendors ? 'spin 1s linear infinite' : 'none',
                              '@keyframes spin': {
                                from: { transform: 'rotate(0deg)' },
                                to: { transform: 'rotate(360deg)' },
                              },
                            }}
                          />
                        </Box>
                      </Box>
                      {matchingMode === 'exact' && canViewVendors && vendorAccountValue?.id && vendorAccountValue?.accounting && !vendorAccountValue?.accounting?.isSynced && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-start"
                          gap="8px"
                          width='92%'
                          sx={{
                            background: (theme) => theme.palette.primary.light,
                            color: (theme) => theme.palette.common.white,
                            fontFamily: 'Inter',
                            fontSize: 15,
                            transition: 'background 0.2s, color 0.2s',
                            // cursor: 'pointer',
                            // padding: '10px 16px',
                            // border: (theme) => `1px solid ${theme.palette.info.main}`,
                            // '&:hover': {
                            //   borderColor: (theme) => theme.palette.info.main,
                            //   background: (theme) => theme.palette.secondary.light,
                            // },
                          }}
                        >
                          <Box display="flex" alignItems="center" gap="8px">
                            <span>{vendorAccountValue.label}</span>
                            <Box
                              sx={{
                                backgroundColor: 'warning.main',
                                color: 'white',
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '8px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Unsynced
                            </Box>
                          </Box>
                          <FormLinkButton
                            path={`/vendors/edit-vendor-account/${vendorAccountValue.id}`}
                          />
                        </Box>
                      )}
                      <Box display="flex" width="100%" flexDirection="column" gap="12px">
                        {canCreateVendors && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                            }}
                          >
                            <AddButton
                              buttonText="Create New Vendor"
                              path="/vendors/add-vendor-account"
                              containedWidth
                            />
                          </Box>
                        )}

                        {/* Exact mode: Show button and link when vendor is unsynced */}
                        {(matchingMode === 'exact' &&
                          isAccountingIntegrated) &&
                          (
                            <>
                              <Box
                                sx={{
                                  '& button': {
                                    height: '35px',
                                    minHeight: '32px',
                                  },
                                  '& p': { fontSize: '14px' },
                                }}
                              >
                                <CommonButton
                                  type="infoMain"
                                  buttonText={`Find Vendor in ${vendorType}`}
                                  disabled={isSelectingNetSuiteVendor}
                                  onButtonClick={() => setIsNetSuitePopupOpen(true)}
                                />
                              </Box>

                            </>
                          )}

                        {/* Fuzzy mode: Show only button when vendor is unsynced (link already in suggestions) */}
                        {(matchingMode === 'fuzzy' && isAccountingIntegrated) &&
                          (
                            <Box
                              sx={{
                                '& button': {
                                  height: '35px',
                                  minHeight: '32px',
                                },
                                '& p': { fontSize: '14px' },
                              }}
                            >
                              <CommonButton
                                type="infoMain"
                                buttonText={`Find Vendor in ${vendorType}`}
                                disabled={isSelectingNetSuiteVendor}
                                onButtonClick={() => setIsNetSuitePopupOpen(true)}
                              />
                            </Box>
                          )}

                        {/* Null mode: Show button on open, then show button + link when unsynced vendor selected */}
                        {(matchingMode === null && isAccountingIntegrated) && (
                          <>
                            <Box
                              sx={{
                                '& button': {
                                  height: '35px',
                                  minHeight: '32px',
                                },
                                '& p': { fontSize: '14px' },
                              }}
                            >
                              <CommonButton
                                type="infoMain"
                                buttonText={`Find Vendor in ${vendorType}`}
                                disabled={isSelectingNetSuiteVendor}
                                onButtonClick={() => setIsNetSuitePopupOpen(true)}
                              />
                            </Box>
                            {vendorAccountValue?.accounting &&
                              !vendorAccountValue?.accounting?.isSynced &&
                              canViewVendors &&
                              vendorAccountValue?.id && (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  gap="8px"
                                  sx={{
                                    cursor: 'pointer',
                                    background: (theme) => theme.palette.secondary.light,
                                    color: (theme) => theme.palette.common.white,
                                    padding: '10px 16px',
                                    border: (theme) => `1px solid ${theme.palette.info.main}`,
                                    fontFamily: 'Inter',
                                    fontSize: 15,
                                    transition: 'background 0.2s, color 0.2s',
                                    '&:hover': {
                                      borderColor: (theme) => theme.palette.info.main,
                                      background: (theme) => theme.palette.secondary.light,
                                    },
                                  }}
                                >
                                  <Box display="flex" alignItems="center" gap="8px">
                                    <span>{vendorAccountValue.label}</span>
                                    <Box
                                      sx={{
                                        backgroundColor: 'warning.main',
                                        color: 'white',
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        borderRadius: '8px',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      Unsynced
                                    </Box>
                                  </Box>
                                  <FormLinkButton
                                    path={`/vendors/edit-vendor-account/${vendorAccountValue.id}`}
                                  />
                                </Box>
                              )}
                          </>
                        )}
                      </Box>
                      {/* Suggestions below autocomplete */}
                      {matchingMode !== 'exact' && vendorSuggestions.length > 0 && (
                        <Box className={classes.vendorSuggestionsBox}>
                          <Box className={classes.vendorSuggestionsTitle}>Suggested Vendors</Box>
                          {vendorSuggestions.map((vendor) => {
                            const vendorName = formMethods.watch('vendorName');
                            const selected =
                              vendorName &&
                              typeof vendorName === 'object' &&
                              'id' in vendorName &&
                              vendorName.id === vendor.id;
                            return (
                              <Box
                                key={vendor.id}
                                className={
                                  selected
                                    ? `${classes.vendorSuggestionItem} ${classes.selectedVendorSuggestion}`
                                    : classes.vendorSuggestionItem
                                }
                                onClick={() => setVendorName(vendor)}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                gap="8px"
                              >
                                <Box display="flex" alignItems="center" gap="8px">
                                  <span>{vendor.label}</span>
                                  {vendor.accounting?.isSynced !== undefined && (
                                    <Box
                                      sx={{
                                        backgroundColor: vendor.accounting.isSynced
                                          ? 'success.main'
                                          : 'warning.main',
                                        color: 'white',
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        borderRadius: '8px',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {vendor.accounting.isSynced ? 'Synced' : 'Unsynced'}
                                    </Box>
                                  )}
                                </Box>
                                {canViewVendors && (
                                  <FormLinkButton
                                    path={`/vendors/edit-vendor-account/${vendor.id}`}
                                  />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      {vendorAccountIdValue && (
                        <>
                          {' '}
                          <FormSelectField
                            name="paymentMethod"
                            label="Payment Method"
                            options={DEDUCTION_PAYMENT_METHOD_OPTIONS}
                            width="100%"
                          />
                          {requiresPaymentAccount && paymentMethodValue && (
                            <>
                              <FormSelectField
                                name='paymentAccount.id'
                                label='Account *'
                                options={paymentAccountOptions}
                                width='100%'
                                renderSelectedValue={renderPaymentAccountSelectedValue}
                                renderOption={renderPaymentAccountOption}
                                customOnChange={(value) => {
                                  const selected = paymentAccountOptions.find((item) => item.id === value);
                                  setValue(
                                    'paymentAccount',
                                    selected
                                      ? { id: selected.id, label: selected.label }
                                      : null,
                                    { shouldDirty: true, shouldValidate: true },
                                  );
                                }}
                              />

                              {renderPaymentAccountInfo(
                                selectedPaymentAccount,
                                vendorAccountIdValue,
                              )}
                            </>
                          )}
                          {(isPaymentMethodMissing || isPaymentProvisioningIssue) && (
                            <Box
                              display="flex"
                              flexDirection="row"
                              alignItems="center"
                              gap="8px"
                              width="100%"
                            >
                              <Typography color="error" variant="body2" mt='8px'>
                                {isPaymentMethodMissing
                                  ? 'Missing Account Details'
                                  : 'Some issues with the passport provisioning.'}
                              </Typography>
                              {canViewVendors && (
                                <FormLinkButton
                                  path={`/vendors/edit-vendor-account/${vendorAccountIdValue}`}
                                />
                              )}
                            </Box>
                          )}
                          {vendorAccountValue?.accounting &&
                            !vendorAccountValue?.accounting?.isSynced && (
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="flex-end"
                                gap="8px"
                                width="100%"
                              ></Box>
                            )}
                        </>
                      )}
                    </>
                  )}

                  {type !== 'Lien' && type !== 'SERVICE_PROVIDER' && (
                    <FormTextField
                      name="description"
                      label="Description"
                      isMultiline
                      isDescription
                    />
                  )}

                  <FormTextField name="amount" label="Amount *" valueDefinition="$" />
                </Box>

                <DialogActions className={classes.popupActions}>
                  <FormSubmitButton
                    disabled={
                      !isValid ||
                      !isDirty ||
                      isFormSubmitting ||
                      Boolean(
                        (type === 'Lien' || type === 'SERVICE_PROVIDER') &&
                        requiresPaymentAccount &&
                        vendorAccountIdValue &&
                        paymentMethodValue &&
                        !paymentAccountValue?.id,
                      ) ||
                      Boolean(
                        (type === 'Lien' || type === 'SERVICE_PROVIDER') &&
                        vendorAccountIdValue &&
                        ((requiresPaymentAccount && isPaymentMethodMissing) ||
                          (requiresPaymentAccount && isPaymentProvisioningIssue) ||
                          (vendorAccountValue?.accounting &&
                            !vendorAccountValue.accounting.isSynced)),
                      )
                    }
                    buttonText="Submit"
                    onClick={handleSubmit(onSubmitForm)}
                  />
                  <FormCancelButton onClick={handleClose} />
                </DialogActions>
              </Box>
            </FormProvider>
          </DialogContent>
        </>
      )}
      <Popup
        isOpen={isFailedAccountConfirmOpen}
        headlineText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.headlineText}
        contentText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.contentText}
        mainButtonText="Yes"
        secondaryButtonText="No"
        onMainButtonClick={handleConfirmFailedAccountSubmit}
        onSecondaryButtonClick={handleCancelFailedAccountSubmit}
      />
      <FindNetSuiteVendorPopup
        open={isNetSuitePopupOpen}
        lienName={vendorAccountValue?.label || ''}
        lienId={Number(vendorAccountValue?.id)}
        searchTerm={vendorAccountValue?.label || ''}
        onClose={() => setIsNetSuitePopupOpen(false)}
        onVendorSelect={handleVendorSelectFromNetSuite}
        synced={
          vendorAccountValue?.id
            ? (vendorAccountValue.accounting?.isSynced ? 'isSynced' : 'unsynced')
            : null
        }
        entityType="Vendor"
        entityId={Number(vendorAccountValue?.id)}
      />
    </Dialog>
  );
};

export default EditDraftDeductionPopup;